import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { smartLinksApi } from '@/api/smartLinks';
import type { SmartLinkListParams } from '@/api/smartLinks';
import type {
  SmartLink,
  SmartLinksState,
  UpdateSmartLinkPayload,
} from '@/types';
import { preserveOwnerName } from './preserveOwnerName';
import { extractApiError } from '@/api/errorStatus';
import { rejectedApiError } from './rejectedApiError';

const initialState: SmartLinksState = {
  smartLinks: [],
  /**
   * WEB-1368 п.6.12: проект, которому принадлежит список на экране. Приём и
   * оговорки — те же, что у воронок (`funnelsSlice`, п.1.4): сравнение
   * СТРОГОЕ, после `pending` привязка стоит всегда, поэтому `null` значит
   * «список ничей», а не «подойдёт любой».
   */
  projectId: null,
  total: 0,
  isLoading: false,
  error: null,
  limit: 20,
  offset: 0,
};

/**
 * WEB-745: fetchSmartLinksThunk now accepts pagination/search params.
 * Backward compatible with the bare-string call
 * (`fetchSmartLinksThunk(projectId)`) — that form fetches with backend
 * defaults (limit=20, offset=0).
 */
export type FetchSmartLinksArg = string | ({ projectId: string } & SmartLinkListParams);

function normalizeFetchArg(
  arg: FetchSmartLinksArg,
): { projectId: string } & SmartLinkListParams {
  return typeof arg === 'string' ? { projectId: arg } : arg;
}

export const fetchSmartLinksThunk = createAsyncThunk(
  'smartLinks/fetchAll',
  async (arg: FetchSmartLinksArg, { rejectWithValue }) => {
    const { projectId, ...params } = normalizeFetchArg(arg);
    try {
      const response = await smartLinksApi.list(projectId, params);
      return response.data;
    } catch (error: unknown) {
      return rejectWithValue(
        extractApiError(error),
      );
    }
  },
);

/**
 * WEB-1368 п.6.12: действие создания A/B-теста удалено — вызывающих у него
 * не было ни одного (окно создания ходит прямо в `smartLinksApi.create`), а
 * рядом с работающим путём оно предлагало ВТОРОЙ: следующий разработчик
 * подключил бы его и получил два расходящихся пути создания. Вместе с
 * действием сняты его обработчики в редьюсере.
 */

export const updateSmartLinkThunk = createAsyncThunk(
  'smartLinks/update',
  async (
    {
      projectId,
      id,
      data,
    }: { projectId: string; id: string; data: UpdateSmartLinkPayload },
    { rejectWithValue },
  ) => {
    try {
      const response = await smartLinksApi.update(projectId, id, data);
      return response.data as SmartLink;
    } catch (error: unknown) {
      return rejectWithValue(
        extractApiError(error),
      );
    }
  },
);

export const deleteSmartLinkThunk = createAsyncThunk(
  'smartLinks/delete',
  async (
    { projectId, id }: { projectId: string; id: string },
    { rejectWithValue },
  ) => {
    try {
      await smartLinksApi.delete(projectId, id);
      return id;
    } catch (error: unknown) {
      return rejectWithValue(
        extractApiError(error),
      );
    }
  },
);

export const activateSmartLinkThunk = createAsyncThunk(
  'smartLinks/activate',
  async (
    { projectId, id }: { projectId: string; id: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await smartLinksApi.activate(projectId, id);
      return response.data as SmartLink;
    } catch (error: unknown) {
      return rejectWithValue(
        extractApiError(error),
      );
    }
  },
);

export const deactivateSmartLinkThunk = createAsyncThunk(
  'smartLinks/deactivate',
  async (
    { projectId, id }: { projectId: string; id: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await smartLinksApi.deactivate(projectId, id);
      return response.data as SmartLink;
    } catch (error: unknown) {
      return rejectWithValue(
        extractApiError(error),
      );
    }
  },
);

const smartLinksSlice = createSlice({
  name: 'smartLinks',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // fetchAll
    builder
      .addCase(fetchSmartLinksThunk.pending, (state, action) => {
        const { projectId } = normalizeFetchArg(action.meta.arg);
        if (state.projectId !== projectId) {
          state.smartLinks = initialState.smartLinks;
          state.total = initialState.total;
        }
        state.projectId = projectId;
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSmartLinksThunk.fulfilled, (state, action) => {
        const { projectId, limit, offset } = normalizeFetchArg(action.meta.arg);
        if (state.projectId !== projectId) return;
        state.isLoading = false;
        state.smartLinks = action.payload.smartLinks;
        state.total = action.payload.total;
        state.limit = limit ?? 20;
        state.offset = offset ?? 0;
      })
      .addCase(fetchSmartLinksThunk.rejected, (state, action) => {
        const { projectId } = normalizeFetchArg(action.meta.arg);
        if (state.projectId !== projectId) return;
        state.isLoading = false;
        state.error = rejectedApiError(action);
      });

    // update / activate / deactivate — replace the matching entry
    const replaceLink = (
      state: SmartLinksState,
      action: { payload: SmartLink },
    ) => {
      state.smartLinks = state.smartLinks.map((sl) =>
        sl.id === action.payload.id ? preserveOwnerName(sl, action.payload) : sl,
      );
    };

    builder
      .addCase(updateSmartLinkThunk.fulfilled, replaceLink)
      .addCase(activateSmartLinkThunk.fulfilled, replaceLink)
      .addCase(deactivateSmartLinkThunk.fulfilled, replaceLink);

    // delete
    builder.addCase(deleteSmartLinkThunk.fulfilled, (state, action) => {
      state.smartLinks = state.smartLinks.filter(
        (sl) => sl.id !== action.payload,
      );
      state.total = Math.max(0, state.total - 1);
    });
  },
});

export default smartLinksSlice.reducer;
